import type { FlattenedRow } from '../../../hooks/useVirtualizedTaskData';

// Rendered heights of the rows VirtualizedTaskList draws. The virtualizer positions every
// row it has not measured yet from estimateRowSize, so these must match what is painted.
// They used to ignore the 1px dividers around task rows (estimate 40, painted 41), so each
// unmeasured row above the viewport was placed 1px too high. A list that remounts (task
// view -> back) has measured nothing, and the saved scrollTop then landed about one row
// further down per 40 rows scrolled (DEV-3227).

/** VirtualizedTaskRow is `h-10`. */
export const TASK_ROW_HEIGHT = 40;
/** The `h-[1px]` divider under every task row, and above the first row of a section. */
export const ROW_DIVIDER_HEIGHT = 1;
/** VirtualizedGroupHeader. */
export const GROUP_HEADER_HEIGHT = 50;
/** Top gap before every group header except the first one. */
export const GROUP_HEADER_GAP = 20;
/** The inline "+ Add Task" row is `h-[40px]`. */
export const ADD_TASK_ROW_HEIGHT = 40;

const isTaskRow = (row: FlattenedRow | undefined) => row?.type === 'task' || row?.type === 'subtask';

/** A task row that opens a section draws an extra divider above itself. */
export const isFirstInSection = (rows: FlattenedRow[], index: number): boolean => {
  if (!isTaskRow(rows[index])) return false;
  const prevRow = rows[index - 1];
  return !prevRow || prevRow.type === 'group-header';
};

export const estimateRowSize = (rows: FlattenedRow[], index: number): number => {
  const row = rows[index];
  if (!row) return TASK_ROW_HEIGHT;
  if (row.type === 'group-header') {
    return index === 0 ? GROUP_HEADER_HEIGHT : GROUP_HEADER_HEIGHT + GROUP_HEADER_GAP;
  }
  if (row.type === 'add-task') return ADD_TASK_ROW_HEIGHT;
  return TASK_ROW_HEIGHT + ROW_DIVIDER_HEIGHT + (isFirstInSection(rows, index) ? ROW_DIVIDER_HEIGHT : 0);
};
